import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  CheckIcon,
  XIcon,
  TrashIcon,
  ClockIcon,
  ExclamationIcon,
  PhoneIcon,
  MailIcon
} from '@heroicons/react/outline';

// Define Contact interface based on backend model
interface Contact {
  id: string;
  name: string;
  company?: string;
  email: string;
  phone?: string;
  message: string;
  interest: string;
  status: 'new' | 'in-progress' | 'completed';
  createdAt: string;
}

const ContactsPage: React.FC = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/contact');
      
      if (response.data.success) {
        setContacts(response.data.data);
      } else {
        setError('Failed to load contacts');
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error || 
        'An error occurred while fetching contacts'
      );
    } finally {
      setLoading(false);
    }
  };

  const updateContactStatus = async (id: string, status: 'new' | 'in-progress' | 'completed') => {
    try {
      const response = await axios.put(`/api/contact/${id}`, { status });
      
      if (response.data.success) {
        // Update the contacts list with the updated contact
        setContacts(prev => 
          prev.map(contact => 
            contact.id === id ? { ...contact, status } : contact
          )
        );
      } else {
        setError('Failed to update contact status');
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error || 
        'An error occurred while updating the contact'
      );
    }
  };

  const deleteContact = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) {
      return;
    }
    
    try {
      const response = await axios.delete(`/api/contact/${id}`);
      
      if (response.data.success) {
        // Remove the deleted contact from the list
        setContacts(prev => prev.filter(contact => contact.id !== id));
      } else {
        setError('Failed to delete contact');
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error || 
        'An error occurred while deleting the contact'
      );
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredContacts = contacts
    .filter(contact => 
      statusFilter === 'all' || contact.status === statusFilter
    )
    .filter(contact =>
      searchQuery === '' ||
      contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (contact.company && contact.company.toLowerCase().includes(searchQuery.toLowerCase())) ||
      contact.interest.toLowerCase().includes(searchQuery.toLowerCase())
    );

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'in-progress':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };
  
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'new':
        return <ExclamationIcon className="w-4 h-4 mr-1" />;
      case 'in-progress':
        return <ClockIcon className="w-4 h-4 mr-1" />;
      case 'completed':
        return <CheckIcon className="w-4 h-4 mr-1" />;
      default:
        return null;
    }
  };

  const content = (
    <div className="pb-20 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground dark:text-dark-foreground">
          Contact Management
        </h1>
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-destructive/10 dark:bg-dark-destructive/10">
          <p className="text-destructive dark:text-dark-destructive">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        <div className="md:flex-1">
          <label htmlFor="search" className="sr-only">Search contacts</label>
          <input
            type="text"
            id="search"
            placeholder="Search by name, email, company or interest..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full p-2 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
          />
        </div>
        <div>
          <label htmlFor="status-filter" className="sr-only">Filter by status</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="p-2 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
          >
            <option value="all">All Statuses</option>
            <option value="new">New</option>
            <option value="in-progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </div>

      {/* Contacts List */}
      <div className="overflow-hidden border rounded-lg shadow border-border dark:border-dark-border">
        {filteredContacts.length === 0 ? (
          <div className="flex items-center justify-center p-8">
            <p className="text-muted-foreground dark:text-dark-muted-foreground">
              No contacts found matching your criteria
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-muted dark:bg-dark-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground dark:text-dark-muted-foreground uppercase tracking-wider">
                    Name/Company
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground dark:text-dark-muted-foreground uppercase tracking-wider">
                    Contact Info
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground dark:text-dark-muted-foreground uppercase tracking-wider">
                    Interest
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground dark:text-dark-muted-foreground uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground dark:text-dark-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground dark:text-dark-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-card dark:bg-dark-card divide-y divide-border dark:divide-dark-border">
                {filteredContacts.map((contact) => (
                  <tr key={contact.id} className="hover:bg-muted/50 dark:hover:bg-dark-muted/50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-foreground dark:text-dark-foreground">
                        {contact.name}
                      </div>
                      {contact.company && (
                        <div className="text-xs text-muted-foreground dark:text-dark-muted-foreground">
                          {contact.company}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-col space-y-1">
                        <div className="flex items-center text-sm text-foreground dark:text-dark-foreground">
                          <MailIcon className="w-4 h-4 mr-1 text-muted-foreground dark:text-dark-muted-foreground" />
                          <a href={`mailto:${contact.email}`} className="hover:underline">
                            {contact.email}
                          </a>
                        </div>
                        {contact.phone && (
                          <div className="flex items-center text-sm text-foreground dark:text-dark-foreground">
                            <PhoneIcon className="w-4 h-4 mr-1 text-muted-foreground dark:text-dark-muted-foreground" />
                            <a href={`tel:${contact.phone}`} className="hover:underline">
                              {contact.phone}
                            </a>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="inline-flex text-sm text-foreground dark:text-dark-foreground">
                        {contact.interest}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-muted-foreground dark:text-dark-muted-foreground">
                        {formatDate(contact.createdAt)}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(contact.status)}`}>
                        {getStatusIcon(contact.status)}
                        {contact.status === 'in-progress' ? 'In Progress' : 
                          contact.status.charAt(0).toUpperCase() + contact.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => updateContactStatus(contact.id, 'new')}
                          className="p-1 text-blue-600 rounded hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-900/30"
                          title="Mark as new"
                          disabled={contact.status === 'new'}
                        >
                          <ExclamationIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => updateContactStatus(contact.id, 'in-progress')}
                          className="p-1 text-yellow-600 rounded hover:bg-yellow-100 dark:text-yellow-400 dark:hover:bg-yellow-900/30"
                          title="Mark as in progress"
                          disabled={contact.status === 'in-progress'}
                        >
                          <ClockIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => updateContactStatus(contact.id, 'completed')}
                          className="p-1 text-green-600 rounded hover:bg-green-100 dark:text-green-400 dark:hover:bg-green-900/30"
                          title="Mark as completed"
                          disabled={contact.status === 'completed'}
                        >
                          <CheckIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => deleteContact(contact.id)}
                          className="p-1 text-red-600 rounded hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30"
                          title="Delete contact"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-foreground dark:text-dark-foreground">
          Loading contacts...
        </div>
      </div>
    );
  }

  return content;
};

export default ContactsPage; 